import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LifeBuoy, Building2, FileText, Users, ArrowRight, Clock, CheckCircle, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

import { API_BASE as API } from '../config';

export default function OperatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/operator/dashboard`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-teal-600 rounded-full animate-spin" />
      </div>
    );
  }

  const sys = data?.system || {};
  const queue = data?.support_queue || {};
  const totalQueue = (queue.pending || 0) + (queue.in_progress || 0) + (queue.returned || 0);

  const systemStats = [
    { label: 'Active Tenants', value: sys.total_tenants, icon: Building2, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Total Claims', value: sys.total_claims, icon: FileText, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'Active Subscriptions', value: sys.active_subscriptions, icon: CheckCircle, color: 'bg-violet-50 text-violet-700 border-violet-200' },
    { label: 'Registered Users', value: sys.total_users, icon: Users, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  ];

  const queueCards = [
    { label: 'Pending', value: queue.pending, icon: Clock, color: 'bg-amber-500', border: 'border-amber-200', status: 'pending' },
    { label: 'In Progress', value: queue.in_progress, icon: AlertCircle, color: 'bg-blue-500', border: 'border-blue-200', status: 'in_progress' },
    { label: 'Returned', value: queue.returned, icon: RotateCcw, color: 'bg-orange-500', border: 'border-orange-200', status: 'returned' },
    { label: 'Resolved', value: queue.resolved, icon: CheckCircle, color: 'bg-green-500', border: 'border-green-200', status: 'resolved' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Banner */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 mb-6" data-testid="operator-welcome">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <LifeBuoy className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                Welcome back, {user?.name?.split(' ')[0] || 'Operator'}
              </h1>
              <p className="text-sm text-gray-500">
                {data?.operator_name || 'All About Claims'} — Platform Operations
              </p>
            </div>
          </div>
        </div>

        {/* Support Queue Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Support Queue</h2>
            <Button
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white text-xs"
              onClick={() => navigate('/support')}
              data-testid="go-to-support-queue"
            >
              Open Queue <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          {totalQueue > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-3 flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {totalQueue} claim{totalQueue !== 1 ? 's' : ''} need{totalQueue === 1 ? 's' : ''} attention
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="queue-summary">
            {queueCards.map(card => (
              <button
                key={card.status}
                onClick={() => navigate(`/support?status=${card.status}`)}
                className={`bg-white border ${card.border} rounded-xl p-4 text-left hover:shadow-md transition-shadow`}
                data-testid={`queue-${card.status}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-lg ${card.color} flex items-center justify-center`}>
                    <card.icon className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* System Overview */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">System Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="system-stats">
            {systemStats.map(stat => (
              <div key={stat.label} className={`border rounded-xl p-4 ${stat.color}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{stat.label}</span>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Info */}
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              Operator team: <span className="font-semibold text-gray-900">{data?.operator_team_size || 0}</span> member{(data?.operator_team_size || 0) !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
